import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Panel";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { openBoundFile } from "@/lib/fileTickets";
import { fromNow } from "@/lib/format";
import { ListChecks, RefreshCw, XCircle, Download, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const STATUS_TONE = { QUEUED: "bg-secondary text-ink-2", RUNNING: "bg-copper-light text-copper", COMPLETED: "bg-success/10 text-success", FAILED: "bg-danger/10 text-danger", CANCELLED: "bg-secondary text-ink-muted" };
const TYPE_LABEL = { intake_turn: "AI intake", pleading_compare: "Pleading comparison", ocr: "OCR", ai_analysis: "AI analysis", export: "Export", classify: "Classification", order_analysis: "Order analysis", retention_purge: "Retention purge", client_import: "Client import", automation_run: "Automation", automation_scan: "Automation scan" };

export function useJobs(params = {}, interval = 3000) {
  const [jobs, setJobs] = useState(null);
  const key = JSON.stringify(params);
  useEffect(() => {
    let alive = true;
    const tick = () => axios.get(`${API}/jobs`, { params }).then((r) => alive && setJobs(r.data)).catch(() => alive && setJobs((j) => j || []));
    tick();
    const t = setInterval(tick, interval);
    return () => { alive = false; clearInterval(t); };
    // eslint-disable-next-line
  }, [key, interval]);
  return jobs;
}

export const enqueueJob = (type, payload) => axios.post(`${API}/jobs`, { type, payload }).then((r) => r.data);

export default function Jobs() {
  const [filter, setFilter] = useState("");
  const jobs = useJobs(filter ? { status: filter } : {});
  const act = async (id, action) => {
    try { await axios.post(`${API}/jobs/${id}/${action}`); toast.success(action === "retry" ? "Job re-queued." : "Job cancelled."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); }
  };
  return (
    <div data-testid="jobs-page">
      <PageHeader title="Background Jobs" subtitle="OCR, AI analysis and exports run durably in the background — they survive restarts and retry on failure.">
        <select className="h-9 rounded-md border border-line bg-card px-2 text-sm" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="jobs-filter">
          <option value="">All</option>{Object.keys(STATUS_TONE).map((s) => <option key={s}>{s}</option>)}
        </select>
      </PageHeader>
      {jobs === null ? <div className="p-6 text-sm text-ink-muted">Loading…</div> : jobs.length === 0 ? <EmptyState icon={ListChecks} title="No background jobs yet" message="Run OCR on a scanned exhibit or analyse a matter and it will appear here." /> : (
        <Panel>
          <div className="divide-y divide-line">
            {jobs.map((j) => <JobRow key={j.id} j={j} onAct={act} />)}
          </div>
        </Panel>
      )}
    </div>
  );
}

export function JobRow({ j, onAct, compact }) {
  const pct = j.progress?.total ? Math.round((j.progress.completed / j.progress.total) * 100) : null;
  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`job-row-${j.id}`}>
      {j.status === "RUNNING" ? <Loader2 className="h-4 w-4 animate-spin text-copper" /> : <ListChecks className="h-4 w-4 text-ink-muted" />}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-sm font-medium text-ink"><span className="truncate">{j.label}</span><span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] text-ink-2">{TYPE_LABEL[j.type]}</span></div>
        <div className="text-xs text-ink-2" data-testid={`job-stage-${j.id}`}>{j.progress?.stage}{pct != null && ` · ${pct}%`} · attempt {j.attempts}/{j.maxAttempts} · {fromNow(j.createdAt)}{j.error && <span className="ml-1 text-danger">· {j.error}</span>}</div>
        {pct != null && j.status === "RUNNING" && <div className="mt-1 h-1 w-full rounded bg-secondary"><div className="h-1 rounded bg-copper transition-[width]" style={{ width: `${pct}%` }} /></div>}
      </div>
      <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold", STATUS_TONE[j.status])} data-testid={`job-status-${j.id}`}>{j.status}</span>
      {!compact && (
        <div className="flex items-center gap-1">
          {(j.status === "FAILED" || j.status === "CANCELLED") && <Button variant="ghost" size="sm" onClick={() => onAct(j.id, "retry")} data-testid={`job-retry-${j.id}`}><RefreshCw className="mr-1 h-3.5 w-3.5" />Retry</Button>}
          {(j.status === "QUEUED" || j.status === "RUNNING" || (j.status === "FAILED" && ["intake_turn", "pleading_compare"].includes(j.type))) && <Button variant="ghost" size="sm" disabled={j.committing} onClick={() => onAct(j.id, "cancel")} data-testid={`job-cancel-${j.id}`}><XCircle className="mr-1 h-3.5 w-3.5" />Cancel</Button>}
          {j.type === "export" && j.status === "COMPLETED" && <Button variant="outline" size="sm" onClick={() => openBoundFile(`/api/jobs/${j.id}/download`)} data-testid={`job-download-${j.id}`}><Download className="mr-1 h-3.5 w-3.5" />Download</Button>}
        </div>
      )}
    </div>
  );
}
